import { mediaIdImageField } from '@/lib/mediaImageField';
import { AttractionCategoryImageCell } from '@/components/attraction-categories';
import type { CrudConfig } from '@startupquickstart/frontend';

// Define type based on our Prisma schema
export interface AttractionCategory {
  id: string;
  name: string;
  description?: string;
  featuredImageId?: string;
  createdAt: string;
  updatedAt: string;
  featuredImage?: {
    id: string;
    fileName: string;
    name: string;
    fileType: string;
    url?: string;
  };
  // Virtual field for large image display on detail pages
  featuredImageLarge?: {
    id: string;
    fileName: string;
    name: string;
    fileType: string;
    url?: string;
  };
}

/**
 * Attraction Category CRUD configuration
 */
export const attractionCategoryCrudConfig: CrudConfig<AttractionCategory> = {
  entityName: 'Attraction Category',
  entityNamePlural: 'Attraction Categories',

  endpoints: {
    list: '/attraction-categories',
    single: (id: string) => `/attraction-categories/${id}`,
    create: '/attraction-categories',
    update: (id: string) => `/attraction-categories/${id}`,
    delete: (id: string) => `/attraction-categories/${id}`,
  },

  fields: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Enter category name',
      validation: {
        min: 1,
        max: 200,
      },
    },
    {
      key: 'description',
      label: 'Description',
      type: 'richtext',
      required: false,
      placeholder: 'Enter description',
    },
    {
      key: 'featuredImageId',
      label: 'Featured Image',
      type: 'image',
      ...mediaIdImageField,
      required: false,
      placeholder: 'Featured Image ID',
    },
    {
      key: 'featuredImage',
      label: 'Image',
      type: 'custom',
      required: false,
      format: (_value: AttractionCategory['featuredImage'], fullRecord?: AttractionCategory) => {
        return <AttractionCategoryImageCell attractionCategory={fullRecord} />;
      },
    },
    {
      key: 'featuredImageLarge',
      label: 'Image',
      type: 'custom',
      required: false,
      format: (_value: AttractionCategory['featuredImage'], fullRecord?: AttractionCategory) => {
        return <AttractionCategoryImageCell attractionCategory={fullRecord} size="lg" />;
      },
    },
    {
      key: 'createdAt',
      label: 'Created At',
      type: 'datetime',
      readonly: true,
    },
    {
      key: 'updatedAt',
      label: 'Updated At',
      type: 'datetime',
      readonly: true,
    },
  ],

  listView: {
    fields: ['featuredImage', 'name', 'createdAt', 'updatedAt'],
    searchableFields: ['name'],
    sortableFields: ['name', 'createdAt', 'updatedAt'],
    defaultSort: {
      field: 'name',
      direction: 'asc',
    },
    itemsPerPage: 10,
    filters: [],
    actions: {
      create: {
        enabled: true,
      },
      view: {
        enabled: true,
      },
      edit: {
        enabled: true,
      },
      delete: {
        enabled: true,
      },
    },
  },

  detailView: {
    sections: [
      {
        title: 'Basic Information',
        description: 'Core attraction category details',
        fields: ['featuredImageLarge', 'name', 'description'],
        defaultExpanded: true,
      },
      {
        title: 'System Information',
        description: 'Timestamps and system data',
        fields: ['createdAt', 'updatedAt'],
        defaultExpanded: true,
      },
    ],
  },

  form: {
    sections: [
      {
        title: 'General',
        description: 'General Information about the Attraction Category',
        fields: ['featuredImageId', 'name', 'description'],
        columns: 1,
      },
    ],
    validation: {
      name: {
        required: true,
        min: 1,
        max: 200,
      },
    },
  },
};
